import type { ReactNode } from 'react';

import { Box, Flex, Text } from '@bonusly/design-system';

import type { BizyUiBlockAction } from '../../../../schemas/bizy-ui-blocks';
import { UiBlockActionBar } from '../ui-block-action-bar';

/**
 * Shared shell for the four user-admin draft/confirm cards (INS-1011):
 * invite, update, deactivate, reactivate. Renders a title, an optional
 * target subtitle (who the action affects), the card body, and the
 * standard {@link UiBlockActionBar} footer (Confirm / Cancel).
 *
 * The cards are deliberately plain (no recognition-style pills) — they
 * present admin-facing structured data, so the visual language is a
 * labelled field list, not a feed post.
 */
export function UserAdminCard({
	title,
	subtitle,
	actions,
	isReadOnly = false,
	fontSize = 'sm',
	children,
}: {
	title: string;
	subtitle?: string;
	actions: readonly BizyUiBlockAction[];
	isReadOnly?: boolean;
	fontSize?: 'sm' | 'md';
	children?: ReactNode;
}) {
	return (
		<Box my={2} px={3} py={3} bg="bg.surface" border="1px solid" borderColor="border.default" borderRadius="md">
			<Text fontSize={fontSize} fontWeight="semibold" color="text.default">
				{title}
			</Text>
			{subtitle && (
				<Text fontSize="xs" color="text.softer" mt={0.5}>
					{subtitle}
				</Text>
			)}
			{children && <Box mt={2}>{children}</Box>}
			{/* oneShot: a user-admin card resolves to exactly one action — once
			    Confirm (or Cancel) is clicked, disable both so a double-click
			    can't fire the same mutation twice. */}
			<UiBlockActionBar actions={actions} areActionsDisabled={isReadOnly} oneShot />
		</Box>
	);
}

/**
 * A single `Label  value` row in a user-admin card body. The label
 * column stays fixed-width-ish via `flexShrink={0}`; the value wraps.
 */
export function UserAdminFieldRow({ label, value, fontSize = 'sm' }: { label: string; value: ReactNode; fontSize?: 'sm' | 'md' }) {
	return (
		<Flex fontSize={fontSize} gap={2} lineHeight="tall" align="baseline">
			<Text as="span" fontSize="inherit" color="text.softer" flexShrink={0}>
				{label}
			</Text>
			<Text as="span" fontSize="inherit" color="text.default" fontWeight="medium" whiteSpace="pre-wrap">
				{value}
			</Text>
		</Flex>
	);
}

/**
 * One before→after diff row on the update card. `before` renders muted
 * with a strikethrough so the change reads at a glance; the arrow and
 * `after` carry the new value.
 */
export function UserAdminChangeRow({ label, before, after, fontSize = 'sm' }: { label: string; before: ReactNode; after: ReactNode; fontSize?: 'sm' | 'md' }) {
	return (
		<Flex fontSize={fontSize} gap={2} lineHeight="tall" align="baseline" flexWrap="wrap">
			<Text as="span" fontSize="inherit" color="text.softer" flexShrink={0}>
				{label}
			</Text>
			<Text as="span" fontSize="inherit" color="text.softer" textDecoration="line-through">
				{before}
			</Text>
			<Text as="span" fontSize="inherit" color="text.softer" aria-hidden>
				→
			</Text>
			<Text as="span" fontSize="inherit" color="text.default" fontWeight="medium">
				{after}
			</Text>
		</Flex>
	);
}

/**
 * Humanize a raw change-field token (`company_admin`, `manager`,
 * `preferred_first_name`, custom-property keys) into a Title Case label
 * for display. The backend emits these tokens verbatim, so there's no
 * fixed enum to translate against — we present them as readable labels.
 */
export function humanizeFieldToken(token: string): string {
	return token
		.replace(/[_-]+/g, ' ')
		.trim()
		.replace(/\b\w/g, (c) => c.toUpperCase());
}
